import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const ChatNotifContext = createContext({ unreadCount: 0, resetUnread: () => {}, subscribe: () => () => {} });

export const useChatNotif = () => useContext(ChatNotifContext);

export function ChatNotifProvider({ children }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const listenersRef = useRef(new Set());
  const wsRef        = useRef(null);
  const retryRef     = useRef(null);
  const location     = useLocation();

  const authToken  = localStorage.getItem('authToken');
  const authemail  = localStorage.getItem('authemail');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Reset badge when user navigates to /chat
  useEffect(() => {
    if (location.pathname.startsWith('/chat')) setUnreadCount(0);
  }, [location.pathname]);

  // Fetch initial unread count on mount (so badge shows correct count right after login)
  useEffect(() => {
    if (!authToken || !backendUrl || !authemail) return;
    fetch(`${backendUrl}/api/newchats`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(chats => {
        if (!Array.isArray(chats)) return;
        const count = chats.filter(chat => {
          if (!chat?.message || !chat?.product_id) return false;
          if (chat.from === authemail) return false;
          const lastRead = localStorage.getItem(`lastRead_${chat.product_id}`);
          if (!lastRead) return true;
          return new Date(chat.createdAt) > new Date(parseInt(lastRead, 10));
        }).length;
        if (count > 0) setUnreadCount(count);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register/unregister a subscriber (e.g. FetchChat, Inbox)
  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const resetUnread = useCallback(() => setUnreadCount(0), []);

  // Open WebSocket when user is logged in
  useEffect(() => {
    if (!authToken || !backendUrl) return;

    // Convert http(s):// → ws(s)://
    const wsUrl = backendUrl.replace(/^http/, 'ws') + `/ws?token=${encodeURIComponent(authToken)}`;

    let destroyed = false;
    let attempt   = 0;

    const connect = () => {
      if (destroyed) return;
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }

      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => { attempt = 0; }; // reset backoff on success

      socket.onmessage = (e) => {
        try {
          const { event, data } = JSON.parse(e.data);

          if (event === 'new_message') {
            const onChatPage = window.location.pathname.startsWith('/chat');
            const onThisChat = window.location.pathname.includes(`/chat/${data.product_id}/`);
            if (!onChatPage || !onThisChat) setUnreadCount((c) => c + 1);
          }

          listenersRef.current.forEach((cb) => cb(event, data));
        } catch (_) {}
      };

      socket.onclose = () => {
        wsRef.current = null;
        if (!destroyed) {
          // Exponential backoff: 1s, 2s, 4s, 8s … capped at 30s
          const delay = Math.min(1000 * 2 ** attempt, 30_000);
          attempt += 1;
          retryRef.current = setTimeout(connect, delay);
        }
      };

      socket.onerror = () => socket.close();
    };

    connect();

    return () => {
      destroyed = true;
      clearTimeout(retryRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, backendUrl]);

  return (
    <ChatNotifContext.Provider value={{ unreadCount, resetUnread, subscribe }}>
      {children}
    </ChatNotifContext.Provider>
  );
}
